import React, { useState } from 'react';
import { motion } from 'framer-motion';
import posthog from 'posthog-js';
import { openAdobeExchange } from '../utils/analytics';
import { initiateCheckout } from '../utils/checkout';
import { isSafariBrowser, scrollReveal } from '../utils/safariMotion';
import { SUPPORTED_LANGUAGE_COUNT } from '../data/supportedLanguages';
import SupportedLanguagesModal from './SupportedLanguagesModal';
import PageMeta from './seo/PageMeta';
import './Pricing.css';

const LANGUAGE_FEATURE = `Supports ${SUPPORTED_LANGUAGE_COUNT} languages`;

const FREE_FEATURES = [
  '200 free monthly credits (~200 seconds of subtitling)*',
  'Credits reset monthly',
  'Word Level Time Modification',
  'Create unlimited styles and presets',
  LANGUAGE_FEATURE,
  'Unlimited Premiere projects',
];

const PRO_FEATURES = [
  '10,000 credits every month (~10,000 seconds)*',
  'Credits reset monthly',
  'Word Level Time Modification',
  'Create unlimited styles and presets',
  LANGUAGE_FEATURE,
  'Unlimited Premiere projects',
  'Priority support',
];

const ULTRA_FEATURES = [
  '50,000 credits every month (~50,000 seconds)*',
  'Credits reset monthly',
  'Word Level Time Modification',
  'Create unlimited styles and presets',
  LANGUAGE_FEATURE,
  'Unlimited Premiere projects',
  'Priority support',
];

const Pricing: React.FC = () => {
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<
    null | 'pro' | 'ultra'
  >(null);
  const safari = isSafariBrowser();

  const handlePurchase = async (plan: 'pro' | 'ultra') => {
    posthog.capture('checkout_started', { source: `pricing_${plan}` });
    setCheckoutLoadingPlan(plan);
    try {
      await initiateCheckout(plan, { source: `pricing_${plan}` });
    } catch (error) {
      console.error('Checkout failed:', error);
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  const renderFeatureLine = (feature: string, index: number) => (
    <motion.li
      key={`${feature}-${index}`}
      className="plan-features-item"
      transition={{ duration: 0.35, delay: index * 0.04 }}
      {...scrollReveal(
        safari,
        { opacity: 0, x: -12 },
        { opacity: 1, x: 0 },
        { margin: '-40px' }
      )}>
      <span className="feature-icon">✓</span>
      <span>
        {feature === LANGUAGE_FEATURE ? (
          <>
            {feature}
            <button
              type="button"
              className="language-modal-hint"
              onClick={() => setIsLanguageModalOpen(true)}
              title="View supported languages">
              ⓘ
            </button>
          </>
        ) : (
          feature
        )}
      </span>
    </motion.li>
  );

  return (
    <section id="pricing" className="pricing">
      <PageMeta
        title="Pricing | Substyle for Premiere Pro"
        description={`Substyle plans for Premiere Pro — Free, Pro, and Ultra. Animated subtitles with support for ${SUPPORTED_LANGUAGE_COUNT} languages including Ukrainian, Spanish, German, and more.`}
        canonicalPath="/pricing"
        keywords={`Substyle pricing, Premiere Pro subtitles, ${SUPPORTED_LANGUAGE_COUNT} languages`}
      />
      <div className="pricing-container">
        <motion.div
          className="pricing-header"
          transition={{ duration: 0.8, ease: 'easeOut' }}
          {...scrollReveal(safari, { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, 'section')}>
          <motion.h1
            className="pricing-title"
            transition={{ duration: 0.6, delay: 0.1 }}
            {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}>
            Pricing
          </motion.h1>
          <motion.p
            className="pricing-subtitle"
            transition={{ duration: 0.6, delay: 0.2 }}
            {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}>
            Choose the plan that fits how you work.
          </motion.p>
        </motion.div>

        <div className="pricing-grid">
          <motion.div
            className="pricing-card"
            transition={{ duration: 0.6, delay: 0.1 }}
            {...scrollReveal(safari, { opacity: 0, y: 24 }, { opacity: 1, y: 0 }, { margin: '-80px' })}>
            <div className="pricing-card-inner">
              <div className="plan-header">
                <h3 className="plan-name">Free</h3>
                <p className="plan-free-label">FREE</p>
                <p className="plan-subtitle">200 free monthly credits</p>
              </div>

              <ul className="plan-features">
                {FREE_FEATURES.map(renderFeatureLine)}
              </ul>

              <div className="pricing-card-cta">
                <motion.button
                  type="button"
                  className="cta-button secondary"
                  onClick={() => openAdobeExchange('pricing_free')}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.2 }}>
                  Install Free
                </motion.button>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="pricing-card popular"
            transition={{ duration: 0.6, delay: 0.2 }}
            {...scrollReveal(safari, { opacity: 0, y: 24 }, { opacity: 1, y: 0 }, { margin: '-80px' })}>
            <div className="popular-badge">Most Popular</div>
            <div className="pricing-card-inner">
              <div className="plan-header">
                <h3 className="plan-name">Pro</h3>
                <div className="plan-price">
                  <span className="price-amount">$9.99</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="plan-subtitle">10,000 credits every month</p>
              </div>

              <ul className="plan-features">
                {PRO_FEATURES.map(renderFeatureLine)}
              </ul>

              <div className="pricing-card-cta">
                <motion.button
                  type="button"
                  className="cta-button primary"
                  onClick={() => handlePurchase('pro')}
                  disabled={checkoutLoadingPlan !== null}
                  whileHover={{ scale: checkoutLoadingPlan ? 1 : 1.03 }}
                  whileTap={{ scale: checkoutLoadingPlan ? 1 : 0.97 }}
                  transition={{ duration: 0.2 }}>
                  {checkoutLoadingPlan === 'pro' ? 'Loading…' : 'Get Pro'}
                </motion.button>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="pricing-card"
            transition={{ duration: 0.6, delay: 0.3 }}
            {...scrollReveal(safari, { opacity: 0, y: 24 }, { opacity: 1, y: 0 }, { margin: '-80px' })}>
            <div className="pricing-card-inner">
              <div className="plan-header">
                <h3 className="plan-name">Ultra</h3>
                <div className="plan-price">
                  <span className="price-amount">$29.99</span>
                  <span className="price-period">/month</span>
                </div>
                <p className="plan-subtitle">50,000 credits every month</p>
              </div>

              <ul className="plan-features">
                {ULTRA_FEATURES.map(renderFeatureLine)}
              </ul>

              <div className="pricing-card-cta">
                <motion.button
                  type="button"
                  className="cta-button secondary"
                  onClick={() => handlePurchase('ultra')}
                  disabled={checkoutLoadingPlan !== null}
                  whileHover={{ scale: checkoutLoadingPlan ? 1 : 1.03 }}
                  whileTap={{ scale: checkoutLoadingPlan ? 1 : 0.97 }}
                  transition={{ duration: 0.2 }}>
                  {checkoutLoadingPlan === 'ultra'
                    ? 'Loading…'
                    : 'Get Ultra'}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="pricing-note"
          transition={{ duration: 0.6 }}
          {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}>
          <p>
            *Credits are an estimate of subtitling time (~1 credit ≈ 1 second) and
            may vary based on project complexity. Credits reset monthly.
            Cancel anytime.
          </p>
          <p className="legal-disclaimer">
            By downloading, purchasing or using Substyle, you agree to our{' '}
            <a
              href="https://codec422.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link">
              Privacy Policy
            </a>
            ,{' '}
            <a
              href="https://codec422.com/eula"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link">
              Licensing Agreement
            </a>{' '}
            and{' '}
            <a
              href="https://codec422.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="legal-link">
              Terms and Conditions
            </a>
            .
          </p>
        </motion.div>
      </div>

      <SupportedLanguagesModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />
    </section>
  );
};

export default Pricing;
